import { ReactNode } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import { GripVertical, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

interface EditableSectionProps {
  id: string;
  children: ReactNode;
  className?: string;
}

const EditableSection = ({ id, children, className }: EditableSectionProps) => {
  const { isEditMode, selectedSection, selectSection } = useEditMode();

  if (!isEditMode) {
    return <div className={className}>{children}</div>;
  }

  const isSelected = selectedSection === id;

  return (
    <div
      className={cn(
        "relative group transition-all duration-200",
        isEditMode && "cursor-pointer",
        isSelected && "ring-2 ring-primary ring-offset-2",
        className
      )}
      onClick={(e) => {
        e.stopPropagation();
        selectSection(id);
      }}
    >
      {/* Edit overlay on hover */}
      <div
        className={cn(
          "absolute inset-0 z-30 pointer-events-none transition-opacity duration-200",
          "bg-primary/5 opacity-0 group-hover:opacity-100",
          isSelected && "opacity-100 bg-primary/10"
        )}
      />

      {/* Top bar with label + controls */}
      <div
        className={cn(
          "absolute top-2 left-2 z-40 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200",
          isSelected && "opacity-100"
        )}
      >
        <div className="flex items-center gap-1 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg">
          <GripVertical size={14} className="cursor-grab" />
          <span className="capitalize">{id}</span>
        </div>
        <button
          className="bg-primary text-primary-foreground p-1.5 rounded-lg shadow-lg hover:bg-primary/90 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            selectSection(id);
          }}
        >
          <Pencil size={14} />
        </button>
      </div>

      {children}
    </div>
  );
};

export default EditableSection;
